// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-COST-6 — one month, every line: revenue per customer, the reserve, each Azure resource (customer DBs /
// shared / unlicensed) and each other cost, down to net profit and the Blue Dev / Owl shares — with last
// month beside it. For the running month it also projects month-end from the daily run rate.
// Pure presentation over the summary the page already loaded; no extra request.

import Box from '@mui/material/Box'
import GroupedLines, { type LineGroup } from '../../../components/ui/GroupedLines'
import SummaryLines from '../../../components/ui/SummaryLines'
import { bucketSplit, type FinanceMonthRow, type FinanceResourceRow, type FinanceSummary } from '../../../api/finance'
import { money } from './FinanceTables'
import { CADENCE_LABEL, monthLabel } from './CostItemDialogs'

/** Days of the month covered by the snapshot (fractional), and the month's length. */
export function monthProgress(month: string, asOfIso: string | null): { elapsed: number; days: number } {
  const [y, m] = month.split('-').map(Number)
  const start = Date.UTC(y, m - 1, 1)
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate()
  const asOf = asOfIso ? Date.parse(asOfIso) : Date.now()
  const elapsed = Math.min(days, Math.max(0.5, (asOf - start) / 86_400_000))
  return { elapsed, days }
}

/** Month-end projection: Azure scales with the run rate; revenue, reserve and other costs are monthly amounts. */
export const project = (azureSoFar: number, elapsed: number, days: number) => Math.round(azureSoFar * (days / elapsed) * 100) / 100

/** Month-end costs and the bucket split they lead to for the running month. Usage-billed costs (Azure, GitHub) scale
 *  with the run rate; revenue and other costs are monthly amounts. Null bucket = the R7 model. */
export function projectedMonth(data: FinanceSummary) {
  const d = data.detail
  if (!d) return null
  const { elapsed, days } = monthProgress(data.month, data.cost_as_of)
  const costs = Math.round((project(d.expenses.azure_usd + d.expenses.github_usd, elapsed, days) + d.expenses.other_usd) * 100) / 100
  const bucket = d.pnl.model === 'bucket' && d.pnl.bucket_pct !== null
    ? bucketSplit(d.pnl.revenue_usd, costs, d.pnl.reserve_pct, d.pnl.bucket_pct) : null
  const net = bucket ? bucket.partners : Math.round((d.pnl.revenue_usd - d.pnl.reserve_usd - costs) * 100) / 100
  return { costs, bucket, net }
}

/** Net profit (the partners' share under the bucket model) at month-end for the running month. */
export function projectedNet(data: FinanceSummary): number | null {
  return projectedMonth(data)?.net ?? null
}

const sum = (xs: number[]) => Math.round(xs.reduce((a, b) => a + b, 0) * 100) / 100

/** Signed change with a colour that means "good" or "bad" for this kind of line. */
function Change({ now, before, higherIsGood }: { now: number; before: number | null; higherIsGood: boolean }) {
  if (before === null) return <Box component="span" sx={{ color: 'text.secondary' }}>—</Box>
  const d = Math.round((now - before) * 100) / 100
  if (d === 0) return <Box component="span" sx={{ color: 'text.secondary' }}>no change</Box>
  const good = higherIsGood ? d > 0 : d < 0
  return <Box component="span" sx={{ color: good ? 'success.main' : 'error.main' }}>{d > 0 ? '▲' : '▼'} {money(Math.abs(d))}</Box>
}

export default function MonthBreakdown({ data }: { data: FinanceSummary }) {
  const d = data.detail
  if (!d) return <Box sx={{ fontSize: 13, color: 'text.secondary' }}>No cost snapshot for {monthLabel(data.month)}.</Box>

  const s = data.settings
  const running = data.month === data.current_month
  const idx = data.history.findIndex(h => h.month === data.month)
  const prev: FinanceMonthRow | null = idx >= 0 ? data.history[idx + 1] ?? null : null
  const { elapsed, days } = monthProgress(data.month, data.cost_as_of)
  const proj = (v: number) => project(v, elapsed, days)

  // Columns: [so far | projected]? + last month + change (projected vs last month while running).
  const columns = running ? ['So far', 'Projected month-end', 'Last month', 'Change'] : ['This month', 'Last month', 'Change']
  const row = (now: number, projected: number | null, before: number | null, higherIsGood: boolean) => {
    const cmp = running && projected !== null ? projected : now
    const cells = [money(now)]
    if (running) cells.push(projected === null ? money(now) : money(projected))
    return [...cells, money(before), <Change key="c" now={cmp} before={before} higherIsGood={higherIsGood} />]
  }

  const azureGroup = (key: string, label: string, bucket: FinanceResourceRow['bucket'], defaultOpen = false): LineGroup => {
    const lines = d.resources.filter(r => r.bucket === bucket)
    const total = sum(lines.map(r => r.cost_usd))
    const before = lines.some(r => r.prior_usd !== null) ? sum(lines.map(r => r.prior_usd ?? 0)) : null
    return {
      key, label: `− ${label}`, defaultOpen, cells: row(total, running ? proj(total) : null, before, false),
      lines: lines.map(r => ({
        key: r.resource, label: r.customer_name ?? r.resource, sub: r.customer_name ? r.resource : r.kind,
        cells: row(r.cost_usd, running ? proj(r.cost_usd) : null, r.prior_usd, false),
      })),
    }
  }

  const licensed = d.customers.filter(c => c.licensed)
  const revLabel = d.income_lines.length ? ` + ${d.income_lines.length} income / credit line${d.income_lines.length === 1 ? '' : 's'}` : ''
  const otherTotal = d.expenses.other_usd
  const projection = running ? projectedMonth(data) : null
  const netProjected = projection?.net ?? d.pnl.net_profit_usd
  const isBucket = d.pnl.model === 'bucket'
  const share = (net: number) => {
    const blue = Math.round(net * d.pnl.dev_split_pct) / 100
    return { blue, owl: Math.round((net - blue) * 100) / 100 }
  }
  const nowShare = { blue: d.pnl.blue_dev_usd, owl: d.pnl.owl_usd }
  const projShare = share(netProjected)

  const groups: LineGroup[] = [
    {
      key: 'rev', label: `Revenue — ${licensed.length} licensed customer${licensed.length === 1 ? '' : 's'}${revLabel}`,
      cells: row(d.pnl.revenue_usd, d.pnl.revenue_usd, prev?.revenue_usd ?? null, true),
      lines: [
        ...licensed.map(c => ({ key: c.pharmacy_id, label: c.name, sub: 'monthly license', cells: row(c.monthly_license_usd ?? 0, c.monthly_license_usd ?? 0, null, true) })),
        ...d.income_lines.map(l => {
          const signed = l.kind === 'credit' ? -l.amount_usd : l.amount_usd
          return {
            key: `inc:${l.item_id}`, label: `${l.kind === 'credit' ? '− ' : '+ '}${l.title}`,
            sub: [l.kind === 'credit' ? 'credit / discount' : 'other income', l.customer_name, CADENCE_LABEL[l.cadence]].filter(Boolean).join(' · '),
            cells: row(signed, signed, null, true),
          }
        }),
      ],
    },
    {
      key: 'res', label: isBucket
        ? `− S·M·L·E (up to ${d.pnl.reserve_pct}% — what the ${d.pnl.bucket_pct}% bucket has left after running costs)`
        : `− S·M·L·E reserve (${s.reserve_pct}% of revenue)`,
      cells: row(d.pnl.reserve_usd, projection?.bucket?.smle ?? d.pnl.reserve_usd, prev?.reserve_usd ?? null, false), lines: [],
    },
    azureGroup('db', 'Azure — customer databases', 'customer', true),
    azureGroup('shared', 'Azure — shared resources', 'overhead'),
    azureGroup('unl', 'Azure — unlicensed / retired databases', 'unlicensed'),
    {
      key: 'gh', label: d.github_available ? '− GitHub' : '− GitHub — not connected yet (see Data sources)',
      cells: d.github_available
        ? row(d.expenses.github_usd, running ? proj(d.expenses.github_usd) : null, prev?.github_usd ?? null, false)
        : running ? ['—', '—', '—', '—'] : ['—', '—', '—'],
      lines: d.github_lines.map(l => ({
        key: `${l.product}:${l.sku}:${l.repo}`, label: `${l.product}${l.sku ? ` · ${l.sku}` : ''}`,
        sub: [l.repo, l.quantity !== null ? `${l.quantity} ${l.unit ?? ''}`.trim() : null].filter(Boolean).join(' · '),
        cells: row(l.net_usd, running ? proj(l.net_usd) : null, null, false),
      })),
    },
    {
      key: 'other', label: `− Other costs (${d.other_lines.length})`,
      cells: row(otherTotal, otherTotal, prev?.other_usd ?? null, false),
      lines: d.other_lines.map(l => ({
        key: l.item_id, label: l.title + (l.vendor ? ` · ${l.vendor}` : ''),
        sub: `${CADENCE_LABEL[l.cadence]}${l.skipped ? ' · skipped this month' : l.overridden ? ` · changed from ${money(l.base_usd)}` : ''}`,
        cells: row(l.amount_usd, l.amount_usd, null, false),
      })),
    },
    ...(isBucket ? [{
      key: 'held', label: `− Held — the bucket left over (running costs ${money(d.expenses.total_usd)} vs target ${money(d.pnl.cost_target_usd)}); split when released`,
      cells: row(d.pnl.held_usd, projection?.bucket?.held ?? d.pnl.held_usd, prev?.model === 'bucket' ? prev.held_usd : null, false), lines: [],
    }] : []),
  ]

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      {data.close?.status === 'closed' && (
        <Box sx={{ fontSize: 12.5, color: 'text.secondary' }}>
          Closed — these are the figures frozen {data.close.frozen_at ? `on ${new Date(data.close.frozen_at).toLocaleString()}` : ''}
          {data.close.version > 1 ? ` (version ${data.close.version})` : ''}. They do not change unless the month is reopened.
        </Box>
      )}
      {running && (
        <Box sx={{ fontSize: 12.5, color: 'text.secondary' }}>
          {monthLabel(data.month)} is still running — {elapsed.toFixed(1)} of {days} days counted.
          “Projected month-end” scales Azure and GitHub by the daily run rate; revenue, the reserve and other costs are monthly amounts and stay as they are.
          “Change” compares the projection with last month.
        </Box>
      )}
      <GroupedLines columns={columns} groups={groups}
        total={{ label: isBucket ? "= Partners' share" : '= Net profit', cells: row(d.pnl.net_profit_usd, running ? netProjected : null, prev?.net_profit_usd ?? null, true) }} />
      <SummaryLines maxWidth={620} lines={[
        { key: 'blue', label: `Blue Dev ${d.pnl.dev_split_pct}%`, value: running ? `${money(nowShare.blue)} so far · ${money(projShare.blue)} projected` : money(nowShare.blue) },
        { key: 'owl', label: `Owl ${100 - d.pnl.dev_split_pct}%`, value: running ? `${money(nowShare.owl)} so far · ${money(projShare.owl)} projected` : money(nowShare.owl), strong: true },
      ]} />
    </Box>
  )
}
